"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ShieldCheck, RefreshCw, ArrowLeft, ExternalLink, Activity } from "lucide-react";

interface InsuranceTopBarProps {
  activeTab: "portal" | "crm";
  currency: "INR" | "USD";
  onCurrencyToggle: (curr: "INR" | "USD") => void;
  onResetDemo?: () => void;
}

export default function InsuranceTopBar({
  activeTab,
  currency,
  onCurrencyToggle,
  onResetDemo,
}: InsuranceTopBarProps) {
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (confirm("Reset Insurance & Wealth demo database to default seeded proposals?")) {
      setResetting(true);
      try {
        await fetch("/api/demos/insurance/reset", { method: "POST" });
        if (onResetDemo) onResetDemo();
        window.location.reload();
      } catch (err) {
        console.error("Reset failed", err);
      } finally {
        setResetting(false);
      }
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-blue-900/40 px-4 lg:px-8 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Brand & Sub-Brand */}
        <div className="flex items-center gap-3">
          <Link
            href="/demos"
            className="flex items-center gap-1.5 text-xs text-blue-400/80 hover:text-blue-300 transition-colors mr-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">All Demos</span>
          </Link>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white shadow-md shadow-blue-900/50">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm lg:text-base text-white tracking-tight">
                  Aegis Prime
                </span>
                <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded bg-blue-950/80 border border-blue-800/60 text-blue-300">
                  Wealth &amp; Insurance
                </span>
              </div>
              <p className="text-[10px] text-slate-400 hidden sm:block">
                IRDAI Reg. 892/2019 • Underwriting &amp; Persistency CRM
              </p>
            </div>
          </div>
        </div>

        {/* Center: Switcher between Portal & CRM */}
        <div className="flex items-center bg-slate-900/90 p-1 rounded-xl border border-slate-800">
          <Link
            href="/demos/insurance"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === "portal"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Client Portal
          </Link>
          <Link
            href="/demos/insurance/crm"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === "crm"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Activity className="w-3 h-3 text-emerald-400" />
            Underwriting CRM
          </Link>
        </div>

        {/* Right Controls: Currency Toggle & Reset Demo */}
        <div className="flex items-center gap-2.5">
          <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg p-0.5 text-xs">
            <button
              onClick={() => onCurrencyToggle("INR")}
              className={`px-2 py-1 rounded font-semibold transition-colors ${
                currency === "INR"
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              ₹ INR
            </button>
            <button
              onClick={() => onCurrencyToggle("USD")}
              className={`px-2 py-1 rounded font-semibold transition-colors ${
                currency === "USD"
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              $ USD
            </button>
          </div>

          <button
            onClick={handleReset}
            disabled={resetting}
            title="Reset Demo Data"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-white bg-slate-900 hover:bg-slate-800 border border-slate-800 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${resetting ? "animate-spin text-blue-400" : ""}`} />
            <span className="hidden md:inline">Reset Demo</span>
          </button>
        </div>
      </div>
    </header>
  );
}
